import {
  COMPLEXITY_FACTORS,
  COMPLEXITY_OPTIONS,
  TIER_COLOURS,
  goalLabel,
  money,
  newId,
  prioritise,
  type Goal,
  type GoalPriority,
  type ValueMap,
} from "@valuemap/core";
import type { Dispatch } from "./EditView.js";
import styles from "./Prioritise.module.css";

/**
 * The Prioritise view: effort and complexity ratings, the value against complexity matrix, tiers
 * and the priority order. Layout, wording and the matrix drawing follow the reference tool; the
 * figures come from `prioritise` in `@valuemap/core`.
 */

interface Props {
  map: ValueMap;
  canEdit: boolean;
  /** Whether this person may remove their own factors (staff only). Defaults to `canEdit`. */
  canDelete?: boolean;
  dispatch: Dispatch;
}

const tierColour = (p: GoalPriority) => `#${TIER_COLOURS[p.tier.key]}`;

function Matrix({ data, maxValue }: { data: GoalPriority[]; maxValue: number }) {
  const W = 640;
  const H = 460;
  const pL = 64;
  const pR = 28;
  const pT = 20;
  const pB = 54;
  const plotW = W - pL - pR;
  const plotH = H - pT - pB;
  const midX = pL + plotW / 2;
  const midY = pT + plotH / 2;
  const quadrant = (x: number, y: number, fill: string) => (
    <rect x={x} y={y} width={plotW / 2} height={plotH / 2} fill={fill} />
  );
  const label = (x: number, y: number, text: string, anchor: "start" | "end", fill: string) => (
    <text
      x={x}
      y={y}
      fontSize="12"
      fontWeight="900"
      letterSpacing="1"
      fill={fill}
      textAnchor={anchor}
      opacity="0.75"
    >
      {text}
    </text>
  );
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      fontFamily="Lato, sans-serif"
      role="img"
      aria-label="Matrix of quantified value against effort and complexity"
    >
      {quadrant(pL, pT, "#F1F9F3")}
      {quadrant(midX, pT, "#EDF6FA")}
      {quadrant(pL, midY, "#F5F8FA")}
      {quadrant(midX, midY, "#FDF6EC")}
      <rect x={pL} y={pT} width={plotW} height={plotH} fill="none" stroke="#C3D5DE" />
      <line x1={midX} y1={pT} x2={midX} y2={pT + plotH} stroke="#DCE6EC" strokeDasharray="4 4" />
      <line x1={pL} y1={midY} x2={pL + plotW} y2={midY} stroke="#DCE6EC" strokeDasharray="4 4" />
      {label(pL + 10, pT + 20, "QUICK WINS", "start", "#57A068")}
      {label(pL + plotW - 10, pT + 20, "BIG BETS", "end", "#0C86BC")}
      {label(pL + 10, pT + plotH - 10, "FILL-INS", "start", "#6B818B")}
      {label(pL + plotW - 10, pT + plotH - 10, "RECONSIDER", "end", "#C6791F")}
      <text
        x={pL + plotW / 2}
        y={H - 16}
        fontSize="13"
        fontWeight="700"
        fill="#123342"
        textAnchor="middle"
      >
        Effort and complexity to achieve
      </text>
      <text x={pL} y={H - 34} fontSize="11" fill="#6B818B">
        Low
      </text>
      <text x={pL + plotW} y={H - 34} fontSize="11" fill="#6B818B" textAnchor="end">
        High
      </text>
      <text
        transform={`translate(18 ${pT + plotH / 2}) rotate(-90)`}
        fontSize="13"
        fontWeight="700"
        fill="#123342"
        textAnchor="middle"
      >
        Quantified value
      </text>
      {maxValue <= 0 && (
        <text
          x={pL + plotW / 2}
          y={pT + plotH / 2}
          fontSize="13"
          fill="#6B818B"
          textAnchor="middle"
          fontStyle="italic"
        >
          Quantify goals to plot their value
        </text>
      )}
      {data.map((d) => {
        const cx = Math.max(1, Math.min(5, d.cx));
        const x = pL + ((cx - 1) / 4) * plotW;
        const vNorm = maxValue > 0 ? d.value / maxValue : 0;
        const y = pT + (1 - vNorm) * plotH;
        return (
          <g key={d.goalId}>
            <circle
              cx={x.toFixed(1)}
              cy={y.toFixed(1)}
              r="15"
              fill={tierColour(d)}
              stroke="#fff"
              strokeWidth="2"
            />
            <text
              x={x.toFixed(1)}
              y={(y + 4).toFixed(1)}
              fontSize="13"
              fontWeight="900"
              fill="#fff"
              textAnchor="middle"
            >
              {d.index + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function RankList({
  goals,
  ranked,
  horizon,
}: {
  goals: readonly Goal[];
  ranked: GoalPriority[];
  horizon: number;
}) {
  return (
    <div className={styles.pRank}>
      <h3>Priority order</h3>
      <ol>
        {ranked.map((d) => {
          const goal = goals[d.index];
          if (!goal) return null;
          const dep = goal.complexity.dependsOn
            ? goals.findIndex((g) => g.id === goal.complexity.dependsOn)
            : -1;
          const depGoal = dep >= 0 ? goals[dep] : undefined;
          const sub =
            (d.value > 0 ? `${money(d.value)} over ${horizon} yr` : "not quantified") +
            ` · effort ${d.cx.toFixed(1)}` +
            (depGoal ? ` · After: ${goalLabel(depGoal, dep)}` : "");
          return (
            <li key={d.goalId}>
              <span className={styles.r} style={{ background: tierColour(d), color: "#fff" }}>
                {d.index + 1}
              </span>
              <span className={styles.nm}>
                {goalLabel(goal, d.index)}
                <small>{sub}</small>
              </span>
              <span className={`${styles.pTier} ${styles[d.tier.key]}`}>{d.tier.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Select({
  value,
  label,
  disabled,
  onChange,
}: {
  value: number;
  label: string;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
    >
      {COMPLEXITY_OPTIONS.map(([v, text]) => (
        <option key={v} value={v}>
          {text}
        </option>
      ))}
    </select>
  );
}

function RateCard({
  goal,
  index,
  data,
  goals,
  canEdit,
  canDelete,
  dispatch,
}: {
  goal: Goal;
  index: number;
  data: GoalPriority;
  goals: readonly Goal[];
  canEdit: boolean;
  canDelete: boolean;
  dispatch: Dispatch;
}) {
  const c = goal.complexity;
  const rate = (path: string) => (value: number) =>
    dispatch({ type: "setField", goalId: goal.id, path: `complexity.${path}`, value });
  return (
    <div className={styles.pCard}>
      <div className={styles.pCardHead}>
        <span className={styles.pNum} style={{ background: tierColour(data) }}>
          {index + 1}
        </span>
        <h3>{goalLabel(goal, index)}</h3>
        <span className={styles.pScore}>
          {`Effort ${data.cx.toFixed(1)} / 5 · ${data.value > 0 ? money(data.value) : "not quantified"}`}
        </span>
      </div>
      <div className={styles.pFactors}>
        {COMPLEXITY_FACTORS.map((f) => (
          <div key={f.key} className={styles.pFactor}>
            <span title={f.help}>{f.label}</span>
            <Select value={c[f.key]} label={f.label} disabled={!canEdit} onChange={rate(f.key)} />
          </div>
        ))}
        {c.custom.map((f) => (
          <div key={f.id} className={styles.pFactor}>
            <input
              className={styles.pFname}
              type="text"
              aria-label="Name of your own factor"
              placeholder="Your own factor"
              value={f.label}
              readOnly={!canEdit}
              onChange={(e) =>
                dispatch(
                  {
                    type: "setCustomFactor",
                    goalId: goal.id,
                    factorId: f.id,
                    field: "label",
                    value: e.target.value,
                  },
                  { coalesce: true },
                )
              }
            />
            <Select
              value={f.value}
              label={f.label || "Your own factor"}
              disabled={!canEdit}
              onChange={(value) =>
                dispatch({
                  type: "setCustomFactor",
                  goalId: goal.id,
                  factorId: f.id,
                  field: "value",
                  value,
                })
              }
            />
            {canDelete && (
              <button
                type="button"
                className={styles.pFxRm}
                onClick={() =>
                  dispatch({ type: "removeCustomFactor", goalId: goal.id, factorId: f.id })
                }
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            className={styles.pAddFactor}
            onClick={() =>
              dispatch({
                type: "addCustomFactor",
                goalId: goal.id,
                factor: { id: newId("f"), label: "", value: 3 },
              })
            }
          >
            + Add your own factor
          </button>
        )}
        <div className={styles.pFactor}>
          <span>Depends on</span>
          <select
            aria-label="Depends on"
            value={c.dependsOn}
            disabled={!canEdit}
            onChange={(e) =>
              dispatch({
                type: "setField",
                goalId: goal.id,
                path: "complexity.dependsOn",
                value: e.target.value,
              })
            }
          >
            <option value="">No dependency</option>
            {goals.map((other, j) =>
              other.id === goal.id ? null : (
                <option key={other.id} value={other.id}>
                  {goalLabel(other, j)}
                </option>
              ),
            )}
          </select>
        </div>
      </div>
    </div>
  );
}

export function PrioritiseView({ map, canEdit, canDelete = canEdit, dispatch }: Props) {
  if (map.goals.length === 0) {
    return (
      <section className={`${styles.prioritise} no-print`}>
        <div className={styles.pWrap}>
          <div className={styles.pHead}>
            <div className="eyebrow">Prioritise</div>
            <h1>Value against complexity</h1>
          </div>
          <div className={styles.pEmptyNote}>
            Add goals in Edit first, then rate and prioritise them here.
          </div>
        </div>
      </section>
    );
  }
  const result = prioritise(map.goals, map.horizon);
  return (
    <section className={`${styles.prioritise} no-print`} aria-label="Prioritise">
      <div className={styles.pWrap}>
        <div className={styles.pHead}>
          <div className="eyebrow">Prioritise</div>
          <h1>Value against complexity</h1>
          <p className={styles.pIntro}>
            Rate how much effort and complexity each goal carries to achieve, then see where it
            lands. Quick wins sit top left: high value for low effort, so do them first. Big bets
            sit top right: worth doing, but plan and resource them. The list orders goals by the
            value they return for the effort they take.
          </p>
        </div>
        <div className={styles.pTop}>
          <div className={styles.pMatrix}>
            <Matrix data={result.goals} maxValue={result.maxValue} />
          </div>
          <RankList goals={map.goals} ranked={result.ranked} horizon={map.horizon} />
        </div>
        <h2 className={styles.pSub}>Rate the effort and complexity</h2>
        <div className={styles.pRate}>
          {map.goals.map((g, i) => {
            const data = result.goals[i];
            return data ? (
              <RateCard
                key={g.id}
                goal={g}
                index={i}
                data={data}
                goals={map.goals}
                canEdit={canEdit}
                canDelete={canDelete}
                dispatch={dispatch}
              />
            ) : null;
          })}
        </div>
      </div>
    </section>
  );
}
